import { ref, onMounted, onUnmounted } from 'vue';
import Data from '@/api/data';
import { ActionV2 } from '@/api/action';
import { utcToLocal } from '@/tools/TimeFunctions';

export function useActionStream(dataID, { timeFormat = '24Hour', enabled = true } = {}) {
    const actionHistory = ref([]);
    const _pendingResults = new Map();
    let _requestStream = null;
    let _resultStream = null;

    let _mounted = true;

    const _mergeEntry = (data, isRequest) => {
        if (data.id === 0) return;

        const existing = actionHistory.value.find(e => e['ID #'] === data.id);

        if (!existing) {
            const newEntry = {
                'ID #': data.id,
                Setting:          isRequest ? data.action_class : null,
                Request:          isRequest ? data.action_type  : null,
                'Time Sent':      isRequest ? utcToLocal(data.messageUTC, timeFormat) : null,
                'Time Completed': !isRequest && data.status === 'OK' ? utcToLocal(data.messageUTC, timeFormat) : null,
                Status:           !isRequest ? data.status : 'PENDING',
                Payload:          !isRequest ? data.payload : null,
            };
            const insertIndex = actionHistory.value.findIndex(e => e['ID #'] < data.id);
            if (insertIndex === -1) {
                actionHistory.value.push(newEntry);
            } else {
                actionHistory.value.splice(insertIndex, 0, newEntry);
            }
        } else {
            if (isRequest) {
                existing.Setting      = data.action_class;
                existing.Request      = data.action_type;
                existing['Time Sent'] = utcToLocal(data.messageUTC, timeFormat);
            } else {
                existing['Time Completed'] = data.status === 'OK' ? utcToLocal(data.messageUTC, timeFormat) : null;
                existing.Status            = data.status;
                existing.Payload           = data.payload;
            }
        }

        if (!isRequest) {
            const pending = _pendingResults.get(data.id);
            if (pending) {
                _pendingResults.delete(data.id);
                if (data.status === 'ERROR') {
                    const msg = typeof data.payload === 'string'
                        ? data.payload
                        : data.payload?.message || data.payload?.error || 'Action failed on device';
                    pending.reject(new Error(msg));
                } else {
                    pending.resolve(data.payload);
                }
            }
        }
    };

    onMounted(async () => {
        if (!enabled) return;

        try {
            const requests = await Data.post({ dataID, subject: 'action_request' });
            if (Array.isArray(requests)) requests.forEach(r => _mergeEntry(r, true));
        } catch (_) { /* stream init failure is non-fatal */ }

        try {
            const results = await Data.post({ dataID, subject: 'action_result' });
            if (Array.isArray(results)) results.forEach(r => _mergeEntry(r, false));
        } catch (_) { /* stream init failure is non-fatal */ }

        try {
            _requestStream = await Data.stream(dataID, 'action_request');
            if (!_mounted) { _requestStream.close(); _requestStream = null; return; }
            _requestStream.addEventListener('added', (event) => {
                JSON.parse(event.data).forEach(r => _mergeEntry(r, true));
            });
        } catch (_) { /* stream init failure is non-fatal */ }

        try {
            _resultStream = await Data.stream(dataID, 'action_result');
            if (!_mounted) { _resultStream.close(); _resultStream = null; return; }
            _resultStream.addEventListener('added', (event) => {
                JSON.parse(event.data).forEach(r => _mergeEntry(r, false));
            });
        } catch (_) { /* stream init failure is non-fatal */ }
    });

    onUnmounted(() => {
        _mounted = false;
        if (_requestStream) { _requestStream.close(); _requestStream = null; }
        if (_resultStream)  { _resultStream.close();  _resultStream  = null; }
        for (const [, pending] of _pendingResults) {
            pending.reject(new Error('Component unmounted'));
        }
        _pendingResults.clear();
    });

    const sendAction = async ({ action_class, action, action_type, payload }) => {
        const response = await ActionV2.post(`/${dataID}`, JSON.stringify({
            action_class,
            action,
            action_type,
            payload,
        }));
        const data = await response.json().catch(() => ({}));
        return { ok: response.ok, action_id: data.action_id, message: data.message };
    };

    const awaitResult = (action_id, { timeout = 30000 } = {}) => {
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                _pendingResults.delete(action_id);
                reject(new Error('Timed out waiting for action result'));
            }, timeout);
            _pendingResults.set(action_id, {
                resolve: (payload) => { clearTimeout(timer); resolve(payload); },
                reject:  (err)     => { clearTimeout(timer); reject(err); },
            });
        });
    };

    return { actionHistory, sendAction, awaitResult };
}
